import { find, toNumber } from "lodash";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ProfileHeader from "@/features/profile/components/ProfileHeader";
import { CompetencyMapSection } from "@/shared/widgets/CompetencyMapSection";
import { PersonalityMapSection } from "@/shared/widgets/PersonalityMapSection";
import { ProfileSummarySection } from "@/features/profile/components/ProfileSummarySection";
import { ProfileValuesSection } from "@/features/profile/components/ProfileValuesSection";
import { useViewer } from "@/features/workspace/hooks/useViewer";
import { useDimensionsWithScores } from "@/features/people/hooks/useDimensionsWithScores";
import { HeaderContent } from "@/shared/widgets/HeaderContent";
import ProfileLayout from "@/features/profile/components/ProfileLayout";
import { Breadcrumbs } from "@/shared/widgets/Breadcrumbs";
import { PDFExportTrigger } from "@/shared/pdf";
import { IconButton } from "@/shared/ui/IconButton";
import ProfileSectionWrapper from "@/features/profile/components/ProfileLayout/ProfileSectionWrapper";
import { DataSection } from "@/shared/widgets/DataSection";
import { getGptFlowKey } from "@/shared/constants/gpt-flow-types";
import {
  usePersonalNotes,
  useProfileCompletion,
  useProfileLayoutProps,
  useAssessmentOrdersData,
} from "@/features/profile/hooks";
import { PersonalProfilePdf } from "@/shared/pdf/Templates/Profiles";
import ProfileEmptyState from "@/features/profile/components/ProfileEmptyState";
import usePerson from "@/features/people/hooks/usePerson";
import { CompetencyInsights } from "@/shared/widgets/InsightsSection/CompetencyInsights";
import { usePersonSurveyConfig } from "@/features/people/hooks/usePersonSurveyConfigSelect";
import { getIsSurveysCompleted } from "@/shared/utils/selectors/getSurveyCompletion";
import { AssessmentSurveyType } from "@/shared/constants/assessmentSurveys";

function ProfileScreen() {
  const { viewer } = useViewer();

  const { config: surveyConfig } = usePersonSurveyConfig();

  const { dimensionsData, loading: loadingDimensions } =
    useDimensionsWithScores(
      {
        personal: true,
        type: surveyConfig,
      },
      [viewer.person]
    );

  const { handleUpsertPerson } = usePerson(viewer.person?.id!);

  const { hasAssessmentSurveys, assessmentOrdersDataList } =
    useAssessmentOrdersData(viewer?.person!, surveyConfig);

  const {
    notes,
    loading: loadingNotes,
    handleUpsertUserNote,
    handleDeleteUserNote,
  } = usePersonalNotes(viewer.person?.id!);

  const summary = find(notes, (n: any) => n.type === "summary");

  const { t } = useTranslation();

  const hasCompletedAllSurveys = useMemo(() => {
    return getIsSurveysCompleted(
      viewer.person?.completed_dim_types as AssessmentSurveyType[],
      surveyConfig
    );
  }, [surveyConfig, viewer.person]);

  const { personalityComplete, competenciesComplete, valuesComplete } =
    useProfileCompletion(dimensionsData);

  const { layoutProps, tagsData, scope, loadingMessage } =
    useProfileLayoutProps({
      contextType: "personal_profile",
      entity: {
        id: viewer.person?.id,
        name: viewer.name,
        first_name: viewer.first_name,
      },
      dimensionsData,
      surveyConfig,
      people: [viewer.person],
      loadingStates: [loadingDimensions],
    });

  return (
    <>
      <HeaderContent>
        <PDFExportTrigger
          documentTitle={t("profile.pdf_document_title", {
            name: viewer.name || "",
          })}
          TriggerComponent={(props) => (
            <IconButton
              color="black"
              icon="Print"
              label={t("profile.print_profile")}
              variant="outline"
              {...props}
            />
          )}
          PDFComponent={
            <PersonalProfilePdf
              dimensions={dimensionsData}
              profile={viewer}
              summary={summary?.note || ""}
            />
          }
        />
      </HeaderContent>
      <Breadcrumbs />
      <ProfileLayout {...layoutProps} loadingMessage={loadingMessage}>
        <ProfileHeader
          initials={viewer.initials || ""}
          name={viewer.name || ""}
          avatarUrl={viewer.avatar_url || ""}
          onContactChange={handleUpsertPerson}
          canEdit
          email={viewer?.person?.email || ""}
          phone={viewer?.person?.phone || ""}
          linkedin={viewer?.person?.linkedin || ""}
        />
        <ProfileEmptyState
          variant="user"
          person={viewer.person}
          surveyConfig={surveyConfig}
          visible={!hasCompletedAllSurveys}
        />
        {hasAssessmentSurveys && (
          <DataSection
            title={t("profile.assessment_orders")}
            description={t("profile.assessment_orders_description")}
            items={assessmentOrdersDataList}
          />
        )}
        <ProfileSectionWrapper label="summary" id="summary">
          <ProfileSummarySection
            onDeleteSummary={() =>
              handleDeleteUserNote(toNumber(summary?.id), "summary")
            }
            onSaveSummary={(text) =>
              handleUpsertUserNote({
                note: text,
                type: "summary",
                id: toNumber(summary?.id),
              })
            }
            loading={loadingNotes}
            summaryContent={summary?.note}
            tagsData={tagsData}
            isDisabled={!hasCompletedAllSurveys}
            updatedAt={summary?.updated_at}
            flowId={getGptFlowKey("personal_profile_summary")}
            scope={scope}
          />
        </ProfileSectionWrapper>
        {personalityComplete && (
          <ProfileSectionWrapper label="personality" id="personality">
            <PersonalityMapSection
              dimensions={dimensionsData.personality}
              context="user"
            />
          </ProfileSectionWrapper>
        )}
        {valuesComplete && (
          <ProfileSectionWrapper label="values" id="values">
            <ProfileValuesSection
              dimensions={dimensionsData.values}
              context="user"
              defaultPersonId={viewer.person?.id!}
            />
          </ProfileSectionWrapper>
        )}
        {competenciesComplete && (
          <ProfileSectionWrapper label="competencies" id="competencies">
            <CompetencyMapSection
              dimensions={dimensionsData.competencies}
              context="user"
            />
            <CompetencyInsights
              context="user"
              person={viewer.person}
              dimensions={dimensionsData.competencies}
            />
          </ProfileSectionWrapper>
        )}
      </ProfileLayout>
    </>
  );
}

export default ProfileScreen;
